import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Modal from "../components/ui/Modal";
import {
  useGetOfficesQuery,
  useCreateOfficeMutation,
  useUpdateOfficeMutation,
  useDeleteOfficeMutation,
} from "apis/office";
import { FaPlus } from "react-icons/fa6";
import OfficeEdit from "components/office/OfficeEdit";
import OfficeView from "components/office/OfficeView";
import Table from "components/ui/Table";
import {
  SHARING_PILL,
  getClosedRecordTypes,
  getSharedRecordTypes,
} from "utils/sharing";

const Office = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  // Table page size
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useGetOfficesQuery();

  const { mutate: createOffice, isLoading: isCreateLoading } =
    useCreateOfficeMutation();

  const { mutate: updateOffice } = useUpdateOfficeMutation();

  const { mutate: deleteOffice, isLoading: isDeleteLoading } =
    useDeleteOfficeMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Office Name",
        isFilter: true,
        minWidth: "180px",
      },
      {
        accessorKey: "officeId",
        header: "Office ID",
        cell: (row) => <span className="text-sm">{row.officeId || "-"}</span>,
        isFilter: true,
      },
      {
        accessorKey: "address",
        header: "Address",
        cell: (row) => (
          <span className="text-sm">
            {row.address && row.suburb
              ? `${row.address}, ${row.suburb}`
              : row.address || row.suburb || "-"}
          </span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "domainId",
        header: "Domain ID",
        cell: (row) => <span className="text-sm">{row.domainId || "-"}</span>,
        isFilter: false,
      },
      {
        accessorKey: "reaId",
        header: "REA ID",
        cell: (row) => <span className="text-sm">{row.reaId || "-"}</span>,
        isFilter: false,
      },
      {
        accessorKey: "jurisdiction",
        header: "Jurisdiction",
        cell: (row) => (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              row.jurisdiction
                ? "bg-blue-100 text-blue-800"
                : "bg-mediumgray text-darkergray"
            }`}
          >
            {row.jurisdiction || "Not set"}
          </span>
        ),
        isFilter: true,
        minWidth: "120px",
      },
      {
        accessorKey: "amlUsername",
        header: "AML Username",
        cell: (row) => (
          <span className="text-sm">{row.amlUsername || "-"}</span>
        ),
        isFilter: true,
        minWidth: "160px",
      },
      {
        accessorKey: "amlPassword",
        header: "AML Password",
        cell: (row) => (
          <span className="text-sm">{row.amlPassword || "-"}</span>
        ),
        isFilter: false,
        minWidth: "160px",
      },
      {
        accessorKey: "sharing",
        header: "Shared Across Teams",
        // Only decisions are listed. A closed entry is a deliberate opt-out
        // from what a level above allowed, so it earns its own pill rather
        // than reading as "not shared".
        cell: (row) => {
          const decided = [
            ...getSharedRecordTypes(row.sharing).map((recordType) => ({
              recordType,
              className: SHARING_PILL.open,
            })),
            ...getClosedRecordTypes(row.sharing).map((recordType) => ({
              recordType,
              className: SHARING_PILL.closed,
            })),
          ];

          if (!decided.length) {
            return <span className="text-sm text-darkgray">Inherit</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {decided.map(({ recordType, className }) => (
                <span
                  key={recordType.key}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}
                >
                  {recordType.name}
                </span>
              ))}
            </div>
          );
        },
        isFilter: false,
        minWidth: "180px",
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (row) => (
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleDateString("en-AU", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Office"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Office"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "100px",
      },
    ],
    [],
  );

  // ── Loading / error states ────────────────────────────────────────────────
  if (isDataPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading offices: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedOffice = data?.find((office) => office._id === selectedId);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const buildFormData = ({ imageFile, ...fields }) => {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    });
    if (imageFile) {
      formData.append("image", imageFile);
    }
    return formData;
  };

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleEditSubmit = (formData) => {
    updateOffice({ id: selectedId, formData: buildFormData(formData) });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = (formData) => {
    createOffice(buildFormData(formData));
    setIsAddOpen(false);
  };

  const handleDelete = () => {
    deleteOffice(selectedId);
    setIsDeleteOpen(false);
  };

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  // Passed as ButtonCmp — renders in the top-right of the Table toolbar
  const AddOfficeButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add Office
    </button>
  );

  return (
    <>
      {/* Edit Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Office"
        className="max-w-3xl"
      >
        <OfficeEdit initialData={selectedOffice} onSubmit={handleEditSubmit} />
      </Modal>

      {/* Add Modal */}
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Office"
        className="max-w-3xl"
      >
        <OfficeEdit initialData={{}} onSubmit={handleCreateSubmit} />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Office"
      >
        <div>
          <p className="mb-4">
            Are you sure you want to delete "{selectedOffice?.name}"? This
            action cannot be undone.
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              No
            </button>
            <button
              onClick={handleDelete}
              className="black-button"
              disabled={isDeleteLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Office Details"
        className="max-w-4xl"
      >
        <OfficeView office={selectedOffice} />
      </Modal>

      {/* Table */}
      <div className="container mx-auto">
        <Table
          title="Offices"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddOfficeButton}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default Office;
